<script lang="ts">
export default {
  name: 'BenchmarkingCharts',
};
</script>

<script setup lang="ts">
import { Benchmark } from '@/types/Benchmark';
import { chartMutedColor, defaultChartColors } from '@/utilities/Color';
import { compactNumber } from '@/utilities/Number';
import { Chart } from './Benchmarking';
import { onMounted, onUnmounted, ref } from 'vue';

const props = defineProps<{
  charts: Chart[];
  accountData: Benchmark[];
  otherData: Benchmark[];
}>();

const chartRenderTimeout = ref<NodeJS.Timeout | null>(null);
const loading = ref(false);

function chartDataSource(field: keyof Benchmark, caption: string, isPercentage: boolean): unknown {
  // Extract values from otherData, filtering out null/undefined values
  const values = props.otherData.map((d) => d[field]).filter((v) => v !== null && v !== undefined);
  if (!values.every((v) => typeof v === 'number')) return null; // Handle case where values are not all numbers

  // Compute quartiles
  if (values.length === 0) return null;
  const maxValue = Math.max(...values);

  let q1 = maxValue * 0.25;
  let q2 = maxValue * 0.5;
  let q3 = maxValue * 0.75;
  let q4 = maxValue;

  // If the max value is in the billions, round each quartile to the nearest 100 million
  // resolves an issue where values in the billions would not be displayed
  if (maxValue >= 1e9) {
    const roundingFactor = 1e8; // 100,000,000
    q1 = Math.round(q1 / roundingFactor) * roundingFactor;
    q2 = Math.round(q2 / roundingFactor) * roundingFactor;
    q3 = Math.round(q3 / roundingFactor) * roundingFactor;
    q4 = Math.round(q4 / roundingFactor) * roundingFactor;
  }

  const accountValue = props.accountData.map((d) => d[field]).find((d) => d) as number;

  const chart = {
    caption: `<b>${caption}</b> <span style="font-weight: lighter; font-size: 9pt">(${compactNumber(props.otherData.length)} Peer Programs)</span>`,
    animation: false,
    showLegend: false,
    decimals: '1',
    formatNumberScale: '1',
    numberPrefix: isPercentage ? '' : '$',
    numberScaleValue: isPercentage ? '0.01' : '1000,1000,1000',
    numberScaleUnit: isPercentage ? '%' : 'K,M,B',
    theme: 'cacs',
    showShadow: '1',
    showColorRangeBorder: '1',
    colorRangeBorderThickness: 0.5,
    colorRangeBorderColor: defaultChartColors[0],
    plotFillColor: defaultChartColors[0],
    targetLineColor: chartMutedColor,
    showValue: 1,
    valueFontBold: '1',

    showTickMarks: '0',
    showTickValues: '1',

    pointerOnTop: '1',
    valueAbovePointer: '1',
    valueBgColor: '#ffffff',
    valueBgAlpha: 90,
    gaugeBorderColor: chartMutedColor,
    gaugeFillMix: '{light-0}',
    labelFontSize: '12',
    labelFontBold: 1,

    upperLimit: q4,
  };

  const pointer: any = [];
  if (accountValue) {
    pointer.push({
      value: accountValue,
      borderThickness: 10,
    });
  }

  return {
    chart,
    colorrange: {
      color: [
        { minValue: 0, maxValue: q1, code: '#fdfdfd' },
        { minValue: q1, maxValue: q2, code: '#d3e1e8' },
        { minValue: q2, maxValue: q3, code: '#fdfdfd' },
        { minValue: q3, maxValue: q4, code: '#d3e1e8' },
      ],
    },

    pointers: {
      pointer,
    },

    trendpoints: {
      point: [
        {
          startvalue: 0,
          color: '#050505',
          displayvalue: 'Low',
          thickness: 0.5,
        },
        {
          startvalue: q1,
          color: '#050505',
          displayvalue: '25th Percentile',
          thickness: 0.5,
        },
        {
          startvalue: q2,
          color: '#050505',
          displayvalue: 'Median',
          thickness: 0.5,
        },
        {
          startvalue: q3,
          color: '#050505',
          displayvalue: '75th Percentile',
          thickness: 0.5,
        },
        {
          startvalue: q4,
          color: '#050505',
          displayvalue: 'High',
          thickness: 0.5,
        },
      ],
    },
  };
}

// This was a workaround to re-render this type of chart, .render() was resulting in a blank chart
async function renderCharts(): Promise<void> {
  if (chartRenderTimeout.value) {
    clearTimeout(chartRenderTimeout.value);
  }

  chartRenderTimeout.value = setTimeout(async () => {
    loading.value = true;
    await new Promise((resolve) => setTimeout(resolve, 1));
    loading.value = false;
  }, 300);
}

// ------------
//   lifecycle
// ------------

onMounted(async () => {
  window.addEventListener('resize', renderCharts);
});

onUnmounted(() => {
  window.removeEventListener('resize', renderCharts);
});
</script>

<template>
  <div
    v-if="!loading"
    class="charts grid gap-x-20 gap-y-8 sm:grid-cols-1 xl:grid-cols-2"
    data-test="visuals">
    <div v-for="chart in charts" :key="chart.fieldName">
      <fusioncharts
        :id="`${chart.fieldName}-chart`"
        :key="chart.fieldName"
        type="hlineargauge"
        width="100%"
        height="150"
        dataFormat="json"
        :dataSource="chartDataSource(chart.fieldName, chart.title, !!chart.isPercentage)"></fusioncharts>
    </div>
  </div>
</template>
